/**
 * The editor's Bill of materials tab (BOM): panels, inverters or microinverters, optimisers, the battery and a racking estimate for the
 * panels as they are now (saved or not), with unit prices from the company's equipment where it has them, and a CSV
 * export. The rules are in bom.ts (the same as the API's).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Download, ExternalLink, Package } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage, unwrap, type S } from "../api/client";
import { useOrg } from "../api/org";
import { usePartnersEnabled } from "../api/queries";
import { Alert, Button, Select, Spinner, useToast } from "../components/ui";
import { useT } from "../i18n/I18nProvider";
import { DirIcon } from "../i18n/mirror";
import { money, num } from "../lib/format";
import { bomCsv, bomFilename, bomLines, bomText, bomTotal, categoryLabel, type Placed } from "./bom.ts";
import type { PanelItem } from "./model.ts";
import "./plan.css";

function saveFile(name: string, text: string) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** "Order parts": the company's distributors (switched on in Financing & suppliers, its default first). The API
 * writes the list in the distributor's format and names its order page (and a prefilled cart where one is documented);
 * nothing is sent to the distributor. */
export function OrderParts({ lines, currency, address, designId, version }: { lines: S["BomLine"][]; currency: string; address: string | null | undefined; designId: string; version: number }) {
  const { orgId, can } = useOrg();
  const toast = useToast();
  const t = useT();
  // Only for companies Solar OS switched "Financing & suppliers" on for; nothing at all otherwise.
  const available = usePartnersEnabled(orgId);
  const partners = useQuery({
    queryKey: ["partners", orgId],
    enabled: !!orgId && available === true,
    queryFn: () => unwrap(api.GET("/v1/orgs/{org_id}/partners", { params: { path: { org_id: orgId! } } })),
  });
  const distributors = (partners.data?.items ?? []).filter((i) => i.partner.kind === "distributor" && i.enabled);
  const [chosen, setChosen] = useState<string | null>(null);
  const pick = chosen ?? distributors[0]?.partner.key ?? null;
  const order = useMutation({
    mutationFn: (key: string) =>
      unwrap(
        api.POST("/v1/orgs/{org_id}/partners/{partner_key}/parts-order", {
          params: { path: { org_id: orgId!, partner_key: key } },
          body: { lines, currency, address: address ?? null, design_id: designId, version },
        }),
      ),
    onSuccess: (o) => {
      saveFile(o.filename, o.csv);
      window.open(o.cart_url ?? o.order_url, "_blank", "noopener");
      toast(t("editor.bom.order.downloaded", { partner: o.partner_name }));
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (!orgId || !available || partners.isLoading) return null;
  if (!distributors.length) {
    return (
      <div className="order-parts small muted">
        <span>
          <Package size={13} />{" "}
          {can("settings_rules", "edit")
            ? t("editor.bom.order.chooseDistributor", { link: (c) => <Link to="/financing-suppliers">{c}</Link> })
            : t("editor.bom.order.adminChoosesDistributor")}
        </span>
      </div>
    );
  }
  const current = distributors.find((d) => d.partner.key === pick) ?? distributors[0];
  return (
    <div className="order-parts">
      <div className="order-parts-controls">
        {distributors.length > 1 && (
          <Select ariaLabel={t("editor.bom.order.distributor")} value={current.partner.key} onChange={setChosen} options={distributors.map((d) => ({ value: d.partner.key, label: d.is_default ? t("editor.bom.order.defaultDistributor", { name: d.partner.name }) : d.partner.name }))} />
        )}
        <Button size="sm" variant="primary" icon={<Package size={14} />} loading={order.isPending} disabled={!lines.length || !can("designs_leads", "edit")} onClick={() => order.mutate(current.partner.key)}>
          {t("editor.bom.order.button", { partner: current.partner.name })}
        </Button>
      </div>
      <span className="small muted">
        {t("editor.bom.order.explain", { partner: current.partner.name, target: current.partner.order?.cart_template ? "cart" : "orderPage" })}{" "}
        <DirIcon>
          <ExternalLink size={11} />
        </DirIcon>
        {current.partner.order?.note ? ` ${current.partner.order.note}` : ""}
      </span>
    </div>
  );
}

export function placedOf(panels: PanelItem[]): Placed[] {
  return panels.map((p) => ({ face_id: p.face_id, orientation: p.orientation, center: [p.center[0], p.center[1]], rotation: p.rotation }));
}

/** `version` is the stored number (the API's); `shownVersion` the number the editor shows, used in the CSV's name. */
export function BomPanel({ designId, version, shownVersion, panels, address, designName }: { designId: string; version: number; shownVersion: number; panels: PanelItem[]; address: string | null | undefined; designName: string | null | undefined }) {
  const t = useT();
  const bom = useQuery({
    queryKey: ["bom", designId, version],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/bom", { params: { path: { design_id: designId }, query: { version } } })),
  });
  const computed = useMemo(() => (bom.data ? bomLines(placedOf(panels), bom.data.context) : null), [bom.data, panels]);
  if (bom.isLoading) return <Spinner label={t("editor.loading")} />;
  if (bom.error || !bom.data || !computed) return <Alert tone="warn" title={t("editor.bom.unavailable")}>{errorMessage(bom.error)}</Alert>;
  const currency = bom.data.currency;
  const total = bomTotal(computed.lines);
  const priced = total != null;
  const download = () => {
    const blob = new Blob([bomCsv(computed.lines, currency, { t, labels: computed.labels })], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = bomFilename(address, designName, shownVersion);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  let last = "";
  return (
    <section className="stack plan-panel">
      <div className="stack bom-head">
        <h3 className="bom-title">{t("editor.bom.title")}</h3>
        <span className="small muted">{t("editor.bom.summary", { panels: panels.length, rows: computed.racking.rows })}</span>
        <Button size="sm" icon={<Download size={14} />} onClick={download} disabled={!computed.lines.length}>
          {t("editor.bom.download")}
        </Button>
      </div>
      <OrderParts lines={computed.lines} currency={currency} address={address} designId={designId} version={version} />
      {/* no price at all (no company prices, no cost-plus price model): no Price column */}
      <table className="bom-table">
        <thead>
          <tr>
            <th>{t("editor.bom.column.item")}</th>
            <th className="num">{t("editor.bom.column.quantity")}</th>
            {priced && <th className="num">{t("editor.bom.column.price")}</th>}
          </tr>
        </thead>
        <tbody>
          {computed.lines.map((l, i) => {
            const head = l.category !== last;
            last = l.category;
            return [
              head ? (
                <tr key={`h${i}`} className="bom-cat">
                  <td colSpan={priced ? 3 : 2}>{categoryLabel(t, l.category)}</td>
                </tr>
              ) : null,
              <tr key={i}>
                <td>
                  <div>{bomText(t, computed.labels[i]?.item) ?? l.item}</div>
                  {(l.model || l.note) && <div className="small muted">{[l.model, computed.labels[i] ? bomText(t, computed.labels[i]!.note) : l.note].filter(Boolean).join(" · ")}</div>}
                </td>
                <td className="num">{num(l.quantity)}</td>
                {priced && <td className="num">{l.total != null ? money(l.total, currency) : <span className="muted">—</span>}</td>}
              </tr>,
            ];
          })}
        </tbody>
        {total != null && (
          <tfoot>
            <tr>
              <td>{t("editor.bom.pricedLines")}</td>
              <td />
              <td className="num">{money(total, currency)}</td>
            </tr>
          </tfoot>
        )}
      </table>
      <p className="small muted">
        {t("editor.bom.rackingNote", { stick: num(bom.data.context.racking.rail_stick_m, 1), spacing: num(bom.data.context.racking.attachment_spacing_m, 1) })}
      </p>
    </section>
  );
}
